import React, { useEffect } from 'react';
import { Center, Container, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography, Text } from '@fiverr-private/typography';
import { globalMixpanelReporter } from '../../../events/mixpanel';
import { globalBigQueryReporter } from '../../../events/bigQuery';
import { globalGrafanaReporter } from '../../../events/grafana';
import { useSocialSigningHandlers } from '../../SocialSigningForm';
import { translate } from '../../../utils/translate';
import { GoogleButton } from '../../GoogleButton';
import { ContinueWithEmailButton } from '../../ContinueWithEmailButton';
import { AppleButton } from '../../AppleButton';
import { Variant } from '../../../shared/constants';
import { OnboardingFooter } from '../../OnboardingFooter';

interface SocialFallbackStepProps {
    onProfileSetupStep(): void;
    onSocialFallbackStep(): void;
    onEmailIdentificationStep(): void;
    goBack?: () => void;
    variant?: Variant;
}

export const SocialFallbackStep: React.FC<SocialFallbackStepProps> = ({
    onEmailIdentificationStep,
    onProfileSetupStep,
    onSocialFallbackStep,
    goBack,
    variant,
}) => {
    const { onSuccess, onError } = useSocialSigningHandlers({
        onProfileSetup: onProfileSetupStep,
        onSocialFallback: onSocialFallbackStep,
    });

    const isOnboarding = variant === 'onboarding';

    useEffect(() => {
        globalGrafanaReporter.count('identification.%{platform}.social_provider_fallback.show');
        globalMixpanelReporter.send('Identification - Social Provider Fallback - Show');
        globalBigQueryReporter.send('identification.social_provider_fallback.show');
    }, []);

    const onContinueWithGoogleClick = () => {
        globalBigQueryReporter.send('google_click', { text: 'social_provider_fallback' });
        globalMixpanelReporter.send('Identification - Social Fallback - Initial - Provider selected');
    };

    const onAppleClick = () => {
        globalBigQueryReporter.send('apple_click', { text: 'social_provider_fallback' });
        globalMixpanelReporter.send('Identification - Social Fallback - Initial - Provider selected');
    };

    const onContinueWithEmailClick = () => {
        globalBigQueryReporter.send('identification.initial.email_click', { text: 'social_provider_fallback' });
        onEmailIdentificationStep();
    };

    return (
        <>
            <Stack
                marginTop={{
                    default: '10',
                    sm: '0',
                }}
                direction="column"
                paddingBottom="10"
                paddingX="0"
                gap="2"
            >
                <Heading size={{ default: 'h_xl', sm: 'h_xxl', md: isOnboarding ? 'h_lg' : 'h_md' }} fontWeight="bold">
                    {translate('steps.social_fallback.title')}
                </Heading>
                <Text>{translate('steps.social_fallback.subtitle')}</Text>
            </Stack>
            <Stack direction="column">
                <Center direction="column" gap="4">
                    <GoogleButton onSuccess={onSuccess} onError={onError} onClick={onContinueWithGoogleClick}>
                        {translate('social.continue_with_google')}
                    </GoogleButton>

                    <ContinueWithEmailButton onClick={onContinueWithEmailClick}>
                        {translate('social.continue_with_email')}
                    </ContinueWithEmailButton>

                    <Typography color="grey_700" size="b_xs" fontWeight="bold" style={{ textTransform: 'uppercase' }}>
                        {translate('social.or')}
                    </Typography>

                    <AppleButton onSuccess={onSuccess} onError={onError} onClick={onAppleClick}>
                        {translate('social.apple')}
                    </AppleButton>
                </Center>
            </Stack>

            {isOnboarding && goBack && (
                <Container marginTop="auto">
                    <OnboardingFooter goBack={goBack}></OnboardingFooter>
                </Container>
            )}
        </>
    );
};
